import React, { useState } from 'react';
import { StyleSheet, View } from 'react-native';
import { Credential } from '../../../types/credentials';
import CardFactory from './components/CardFactory';
import { animated, useSpring } from '@react-spring/web';
import { useAuth } from 'react-oidc-context';

interface CredentialCardProps {
  credential: Credential;
  _detailsModal?: boolean;
  _detailsModalClose?: () => void;
}

/**
 * @description Credential card component
 * @param {CredentialCardProps} { credential }
 * @return {React.FC}
 */
const CredentialCard: React.FC<CredentialCardProps> = ({
  credential,
  _detailsModal = false,
  _detailsModalClose
}) => {
  const authContext = useAuth();

  const fullname =
    authContext.user?.profile.given_name + ' ' + authContext.user?.profile.family_name;
  const [isFlipped, setIsFlipped] = useState(_detailsModal);

  const { transform } = useSpring({
    transform: `perspective(600px) rotateY(${isFlipped ? 180 : 0}deg)`,
    config: { mass: 5, tension: 500, friction: 90 }
  });

  return (
    <View style={styles.cardContainer}>
      <animated.div
        style={{
          ...styles.cardContainer,
          transform,
          transformStyle: 'preserve-3d'
        }}>
        <View style={[styles.credentialCard, styles.cardFront]}>
          {CardFactory.createCard(
            fullname,
            credential,
            () => setIsFlipped(!isFlipped),
            false,
            _detailsModal,
            _detailsModalClose
          )}
        </View>
        <View style={[styles.credentialCard, styles.cardBack]}>
          {CardFactory.createCard(
            fullname,
            credential,
            () => setIsFlipped(!isFlipped),
            true,
            _detailsModal,
            _detailsModalClose
          )}
        </View>
      </animated.div>
    </View>
  );
};

const styles = StyleSheet.create({
  cardContainer: {
    position: 'relative',
    width: '100%',
    height: '100%',
    aspectRatio: '16/9',
    maxWidth: 500,
    marginRight: 'auto',
    marginLeft: 'auto'
  },
  credentialCard: {
    position: 'absolute',
    width: '100%',
    height: '100%',
    padding: 16,
    borderRadius: 10,
    overflow: 'hidden',
    boxShadow: '0px 0px 0px 1px rgba(255, 255, 255, 0.42)',
    backgroundColor: '#3b4048',
    backfaceVisibility: 'hidden'
  },
  cardFront: {
    transform: 'rotateY(0deg)'
  },
  cardBack: {
    transform: 'rotateY(180deg)'
  }
});

export default CredentialCard;
